import { createWidgetMountRoot } from './adapters/shadow-root';
import { WidgetEventsBus } from './core/events';
import { resolveMountOptions, resolveRuntimeConfig } from './core/guards';
import { createPolling, type PollingController } from './core/polling';
import { WidgetStateStore } from './core/state';
import { createCountdown, type CountdownController } from './core/timer';
import { ApiClient } from './api/client';
import { SessionApi } from './api/session';
import { renderWidgetScreen, type RenderResult } from './ui/render';
import type { ResolvedWidgetMountOptions, WidgetMountOptions, WidgetRuntimeConfig } from './types/config';
import type { WidgetPaymentStatusResponse, WidgetUiState } from './types/state';

export interface WidgetInstance {
  destroy: () => void;
  refresh: () => Promise<void>;
  getState: () => WidgetUiState;
}

function isFinalStatus(status: string | null | undefined): boolean {
  return ['success', 'partial_payment', 'overpayment', 'expired', 'cancelled', 'failed'].includes(
    String(status)
  );
}

function mapPaymentStatusToUiState(status: string | null | undefined): WidgetUiState {
  switch (status) {
    case 'created':
    case 'method_selected':
      return 'method_select';
    case 'address_assigned':
      return 'address_assigned';
    case 'awaiting_payment':
      return 'awaiting_payment';
    case 'payment_detected':
      return 'payment_detected';
    case 'confirming':
      return 'payment_confirming';
    case 'success':
      return 'success';
    case 'partial_payment':
      return 'partial_payment';
    case 'overpayment':
      return 'overpayment';
    case 'expired':
      return 'expired';
    case 'cancelled':
      return 'cancelled';
    case 'failed':
      return 'failed';
    default:
      return 'network_error';
  }
}

export class CryptoPayWidgetRuntime implements WidgetInstance {
  private readonly options: ResolvedWidgetMountOptions;
  private readonly runtimeConfig: WidgetRuntimeConfig;
  private readonly root: ShadowRoot | HTMLElement;
  private readonly stateStore = new WidgetStateStore();
  private readonly eventsBus: WidgetEventsBus;
  private readonly sessionApi: SessionApi;

  private polling: PollingController | null = null;
  private countdown: CountdownController | null = null;
  private renderResult: RenderResult = {};
  private destroyed = false;
  private lastEmittedStatus: string | null = null;

  constructor(options: WidgetMountOptions) {
    this.options = resolveMountOptions(options);
    this.runtimeConfig = resolveRuntimeConfig();

    const mountRoot = createWidgetMountRoot(this.options.container, true);
    this.root = mountRoot.root;

    const client = new ApiClient({
      baseUrl: this.runtimeConfig.apiBaseUrl
    });

    this.sessionApi = new SessionApi(client);
    this.eventsBus = new WidgetEventsBus(this.options);
  }

  async init(): Promise<void> {
    this.stateStore.setUiState('loading');
    await this.render();

    this.eventsBus.emit('ready');

    try {
      const session = await this.sessionApi.createSession(this.options);

      if (this.destroyed) {
        return;
      }

      this.stateStore.setSession(session);
      this.eventsBus.emit('payment_created', { session });

      if (session.availableMethods.length > 0) {
        this.eventsBus.emit('methods_loaded', { session });
      }

      if (session.status === 'created' || session.status === 'method_selected') {
        this.stateStore.setUiState('method_select');
        await this.render();
        return;
      }

      await this.refresh();
    } catch (error) {
      this.stateStore.setError(error instanceof Error ? error.message : 'Failed to initialize widget');
      this.stateStore.setUiState('network_error');
      this.eventsBus.emit('payment_failed', {
        status: 'failed'
      });
      await this.render();
    }
  }

  async selectMethod(methodCode: string): Promise<void> {
    const session = this.stateStore.getState().session;

    if (!session) {
      throw new Error('Session is not initialized');
    }

    this.stateStore.setUiState('loading');
    await this.render();

    try {
      const selection = await this.sessionApi.selectMethod(session.paymentUuid, methodCode);

      if (this.destroyed) {
        return;
      }

      this.stateStore.setSelection(selection);
      this.stateStore.setUiState(mapPaymentStatusToUiState(selection.status));

      this.eventsBus.emit('method_selected', {
        session,
        selection,
        paymentMethod: selection.paymentMethod
      });

      this.eventsBus.emit('awaiting_payment', {
        session,
        selection,
        status: selection.status
      });

      this.startCountdown(selection.expiresAt);
      this.startPolling();

      await this.render();
    } catch (error) {
      this.stateStore.setError(error instanceof Error ? error.message : 'Failed to select payment method');
      this.stateStore.setUiState('network_error');
      await this.render();
    }
  }

  async refresh(): Promise<void> {
    const session = this.stateStore.getState().session;

    if (!session) {
      return;
    }

    try {
      const status = await this.sessionApi.getStatus(session.paymentUuid);

      if (this.destroyed) {
        return;
      }

      this.applyStatus(status);
      await this.render();
    } catch (error) {
      this.stateStore.setError(error instanceof Error ? error.message : 'Failed to refresh payment status');
      this.stateStore.setUiState('network_error');
      await this.render();
    }
  }

async cancel(): Promise<void> {
  const session = this.stateStore.getState().session;

  if (!session) {
    return;
  }

  this.stopPolling();
  this.stopCountdown();
  this.stateStore.setUiState('loading');
  await this.render();

  try {
    const status = await this.sessionApi.cancel(session.paymentUuid);

    if (this.destroyed) {
      return;
    }

    this.applyStatus(status);
    await this.render();
  } catch (error) {
    this.stateStore.setError(error instanceof Error ? error.message : 'Failed to cancel payment');
    this.stateStore.setUiState('network_error');
    await this.render();
  }
}

  getState(): WidgetUiState {
    return this.stateStore.getUiState();
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.stopPolling();
    this.stopCountdown();

    if ('replaceChildren' in this.root) {
      this.root.replaceChildren();
    }

    this.eventsBus.emit('widget_destroyed', {
      status: this.stateStore.getState().status?.status ?? null
    });
  }

  private applyStatus(status: WidgetPaymentStatusResponse): void {
    this.stateStore.setStatus(status);
    this.stateStore.setUiState(mapPaymentStatusToUiState(status.status));

    const selection = this.stateStore.getState().selection;
    const session = this.stateStore.getState().session;

    if (status.expiresAt && !this.countdown && !isFinalStatus(status.status)) {
      this.startCountdown(status.expiresAt);
    }

    if (isFinalStatus(status.status)) {
      this.stopPolling();
      this.stopCountdown();
    }

    if (this.lastEmittedStatus !== status.status) {
      this.lastEmittedStatus = status.status;

      switch (status.status) {
        case 'payment_detected':
          this.eventsBus.emit('payment_detected', {
            session,
            selection,
            statusPayload: status
          });
          break;
        case 'confirming':
          this.eventsBus.emit('payment_confirming', {
            session,
            selection,
            statusPayload: status
          });
          break;
        case 'success':
          this.eventsBus.emit('payment_success', {
            session,
            selection,
            statusPayload: status
          });
          break;
        case 'expired':
          this.eventsBus.emit('payment_expired', {
            session,
            selection,
            statusPayload: status
          });
          break;
        case 'cancelled':
          this.eventsBus.emit('payment_cancelled', {
            session,
            selection,
            statusPayload: status
          });
          break;
        case 'failed':
        case 'partial_payment':
        case 'overpayment':
          this.eventsBus.emit('payment_failed', {
            session,
            selection,
            statusPayload: status
          });
          break;
        default:
          break;
      }
    }

    if (['awaiting_payment', 'payment_detected', 'confirming'].includes(status.status)) {
      this.startPolling();
    }
  }

  private startPolling(): void {
    if (this.polling?.isRunning()) {
      return;
    }

    this.polling = createPolling({
      intervalMs: this.runtimeConfig.pollIntervalMs,
      task: async () => {
        const session = this.stateStore.getState().session;
        if (!session) {
          return;
        }

        const status = await this.sessionApi.getStatus(session.paymentUuid);

        if (this.destroyed) {
          return;
        }

        this.applyStatus(status);
        await this.render();
      },
      onError: (error) => {
        this.stateStore.setError(error instanceof Error ? error.message : 'Polling failed');
      }
    });

    this.polling.start();
  }

  private stopPolling(): void {
    this.polling?.stop();
    this.polling = null;
  }

  private startCountdown(expiresAt: string): void {
    this.stopCountdown();

    this.countdown = createCountdown({
      expiresAt,
      onTick: (remainingMs) => {
        this.renderResult.setRemainingMs?.(remainingMs);
      },
      onExpire: () => {
        this.stateStore.setUiState('expired');
        this.stopPolling();
        void this.render();
      }
    });

    this.countdown.start();
  }

  private stopCountdown(): void {
    this.countdown?.stop();
    this.countdown = null;
  }

  private async render(): Promise<void> {
    this.renderResult = await renderWidgetScreen(this.root, this.stateStore.getState(), {
      onMethodSelect: async (methodCode) => {
        await this.selectMethod(methodCode);
      },
      onCancelPayment: async () => {
        await this.cancel();
      }
    });

    if (this.countdown && this.renderResult.setRemainingMs) {
      this.renderResult.setRemainingMs(this.countdown.getRemainingMs());
    }
  }
}